/* ─────────────────────────────────────────────────────────────
   Bulk Import — client wizard store.

   URL owns navigation (`step` + `method` search params). This Zustand store
   owns only the transient payload (file, importId, upload status). Reset on
   wizard unmount so re-entry starts clean. Server state (rows, commit result)
   lives in TanStack Query.
   ───────────────────────────────────────────────────────────── */

import { create } from 'zustand'

// ── Shared wizard types (URL search-param values) ─────────────────

export type ImportMethod = 'own-file' | 'template'

export type WizardStep =
  | 'method'
  | 'upload'
  | 'processing'
  | 'mapping'
  | 'structure'
  | 'review'
  | 'complete'

export const STEP_KEYS: WizardStep[] = [
  'method',
  'upload',
  'processing',
  'mapping',
  'structure',
  'review',
  'complete',
]

// ── Store ─────────────────────────────────────────────────────────

// Upload statuses for the 3-step GCS protocol
export type UploadStatus =
  | 'idle'
  | 'requesting'  // awaiting requestEntityImportUpload response
  | 'uploading'   // PUT to GCS signed URL in progress
  | 'confirming'  // awaiting confirmEntityImportUpload response
  | 'done'
  | 'error'

interface BulkImportState {
  fileName: string | null
  fileSize: number | null
  /** Cross-route file staging: set by Day0 dropzone, consumed once by BulkImportWizard on mount. */
  stagedFile: File | null
  importId: string | null
  uploadStatus: UploadStatus
  uploadError: string | null

  setFile: (file: File | null) => void
  setStagedFile: (file: File | null) => void
  setImportId: (importId: string | null) => void
  setUploadStatus: (status: UploadStatus, error?: string | null) => void
  reset: () => void
}

const INITIAL = {
  fileName: null,
  fileSize: null,
  stagedFile: null as File | null,
  importId: null as string | null,
  uploadStatus: 'idle' as UploadStatus,
  uploadError: null as string | null,
}

export const useBulkImportStore = create<BulkImportState>((set) => ({
  ...INITIAL,

  setFile: (file) =>
    set({ fileName: file?.name ?? null, fileSize: file?.size ?? null }),
  setStagedFile: (file) => set({ stagedFile: file }),
  setImportId: (importId) => set({ importId }),
  setUploadStatus: (status, error = null) =>
    set({ uploadStatus: status, uploadError: error ?? null }),
  reset: () => set({ ...INITIAL }),
}))

/** True when the wizard holds unsaved work worth confirming before discard. */
export function hasDirtyState(s: BulkImportState): boolean {
  return Boolean(s.fileName) || Boolean(s.importId)
}
